import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { MatchWithJob, ApplicationStatus, CandidateMatchState } from "@jobreel/shared";
import { toast } from "../store/toast";

/** The minimum a match must carry to be tracked: BOTH ids. `POST /applications`
 *  requires jobId (matchId is optional back-ref), so a mutation keyed only on
 *  matchId 400s. Accept the match object so both ids travel. MatchWithJob (and
 *  the dashboard's looser row) both satisfy this. */
export type TrackableMatch = { jobId: string; matchId: string };

interface MatchFilters {
  minScore?: number;
  workAuth?: string;
  limit?:   number;
  offset?:  number;
}

interface StatsResponse {
  totalMatches:  number;
  avgScore:      number;
  autoApply:     number;
  applied:       number;
  topScore:      number;
}

export function useMatches(filters: MatchFilters = {}) {
  return useQuery<MatchWithJob[]>({
    queryKey: ["matches", filters],
    queryFn:  async () => {
      const { data } = await axios.get("/api/matches", { params: filters });
      return data;
    },
  });
}

export interface MatchesPage {
  items: MatchWithJob[];
  total: number;   // post-filter total across ALL pages (drives page numbers + jump-to-last)
}

/**
 * Paginated variant of useMatches: returns the page rows PLUS the total match
 * count (read from the X-Total-Count response header). Kept separate from
 * useMatches so the array-returning consumers (Dashboard, ApplyQueue) are
 * unaffected. Falls back to the page length if the header is missing.
 */
export function useMatchesPage(filters: MatchFilters = {}) {
  return useQuery<MatchesPage>({
    queryKey: ["matches-page", filters],
    queryFn:  async () => {
      const res = await axios.get<MatchWithJob[]>("/api/matches", { params: filters });
      const header = Number(res.headers["x-total-count"]);
      const total = Number.isFinite(header) ? header : res.data.length;
      return { items: res.data, total };
    },
    placeholderData: (prev) => prev,   // keep the current page visible while the next loads (no fls)
  });
}

export function useMatchDetail(matchId: string | null) {
  return useQuery<MatchWithJob>({
    queryKey: ["match", matchId],
    queryFn:  async () => {
      const { data } = await axios.get(`/api/matches/${matchId}`);
      return data;
    },
    enabled: !!matchId,
  });
}

export function useMatchStats() {
  return useQuery<StatsResponse>({
    queryKey: ["match-stats"],
    queryFn:  async () => {
      const { data } = await axios.get("/api/matches/stats/summary");
      return data;
    },
  });
}

// ── Candidate match readiness ("why do I see no matches?") ──────────────────
//
// GET /matches/readiness distinguishes the actual cause (ambiguous target
// role / matching disabled / thin job inventory / never generated / already
// have matches) instead of Dashboard/Matches collapsing everything into one
// generic "No matches" line. See packages/shared/candidate-match-state.ts.

export interface CandidateReadiness {
  state: CandidateMatchState;
  matchCount: number;
}

export function useCandidateReadiness() {
  return useQuery<CandidateReadiness>({
    queryKey: ["candidate-readiness"],
    queryFn:  async () => {
      const { data } = await axios.get("/api/matches/readiness");
      return data;
    },
    retry: false,
  });
}

export type GenerateMatchesStatus = "disabled" | "no_embedding" | "empty_pool" | "no_matches" | "populated";
export interface GenerateMatchesResult { status: GenerateMatchesStatus; inserted: number }

/** The "Refresh Matches" action — ALWAYS recomputes, even if matches already
 *  exist (re-runs the SAME $0, idempotent matching engine). Used for a
 *  manual refresh once the candidate already has matches, and as the retry
 *  affordance after a genuine "no safe matches" outcome. NOT used for the
 *  first match set anymore — see useEnsureFirstMatchGeneration for that. */
export function useGenerateMatches() {
  const qc = useQueryClient();
  return useMutation<GenerateMatchesResult>({
    mutationFn: async () => {
      const { data } = await axios.post("/api/matches/generate");
      return data;
    },
    onSuccess: (data) => {
      if (data.status === "populated") {
        qc.invalidateQueries({ queryKey: ["matches"] });
        qc.invalidateQueries({ queryKey: ["matches-page"] });
        qc.invalidateQueries({ queryKey: ["match-stats"] });
      }
      qc.invalidateQueries({ queryKey: ["candidate-readiness"] });
    },
  });
}

export interface EnsureFirstMatchResult extends GenerateMatchesResult { ran: boolean }

/** The AUTOMATIC first-match action — no button. Called once by
 *  MatchReadinessPanel whenever readiness reports ready_to_generate; safe to
 *  call again on refresh/login/poll because the server re-checks at call
 *  time whether a current match set already exists for this résumé and
 *  no-ops ("ran: false") instead of recomputing if so. */
export function useEnsureFirstMatchGeneration() {
  const qc = useQueryClient();
  return useMutation<EnsureFirstMatchResult>({
    mutationFn: async () => {
      const { data } = await axios.post("/api/matches/ensure-generated");
      return data;
    },
    onSuccess: (data) => {
      if (data.ran && data.status === "populated") {
        qc.invalidateQueries({ queryKey: ["matches"] });
        qc.invalidateQueries({ queryKey: ["matches-page"] });
        qc.invalidateQueries({ queryKey: ["match-stats"] });
      }
      qc.invalidateQueries({ queryKey: ["candidate-readiness"] });
    },
  });
}

export function useApplyMutation() {
  const qc = useQueryClient();
  return useMutation({
    // Send jobId (required by POST /applications) + matchId (back-ref). Passing
    // only matchId is what made Mark-applied 400 on Dashboard/Matches.
    mutationFn: async (match: TrackableMatch) => {
      const { data } = await axios.post("/api/applications", {
        jobId: match.jobId, matchId: match.matchId, status: "applied",
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
      qc.invalidateQueries({ queryKey: ["match-stats"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useSaveMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (match: TrackableMatch) => {
      const { data } = await axios.post("/api/applications", {
        jobId: match.jobId, matchId: match.matchId, status: "saved",
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Saved to your list");
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
      qc.invalidateQueries({ queryKey: ["applications"] });
    },
    onError: (err) => {
      // Already saved (unique user_id+job_id) → 409; treat as an idempotent success.
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 409) toast.info("Already in your list");
      else toast.error("Couldn't save — please try again");
    },
  });
}

// ── Application tracker (the "I applied myself, now track it" list) ──────────
//
// The tracker reads GET /applications (previously written-only, never read back)
// and advances status via PATCH /applications/:id. Status can NOT be advanced by
// re-POSTing — (user_id, job_id) is unique, so a second POST 409s. PATCH is the
// only path that progresses applied → interviewing → offer → rejected.

/** One row of GET /api/applications — the user's tracked applications. */
export interface TrackedApplication {
  appId:      string;
  status:     ApplicationStatus;
  appliedAt:  string | null;
  notes:      string | null;
  retryCount: number;
  jobId:      string;
  title:      string;
  company:    string;
  location:   string | null;
  url:        string | null;
  score:      number | null;
}

/** The user's tracked applications (optionally filtered to one status). */
export function useApplications(status?: ApplicationStatus) {
  return useQuery<TrackedApplication[]>({
    queryKey: ["applications", status ?? "all"],
    queryFn:  async () => {
      const { data } = await axios.get("/api/applications", {
        params: status ? { status } : undefined,
      });
      return data;
    },
  });
}

/** Advance a tracked application's status via PATCH (NOT a re-POST, which 409s). */
export function useUpdateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ appId, status }: { appId: string; status: ApplicationStatus }) => {
      const { data } = await axios.patch(`/api/applications/${appId}`, { status });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["match-stats"] });
    },
  });
}

/** Remove an application row entirely (un-track). The job returns to the queue as
 *  a match, so re-matching also refreshes. */
export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (appId: string) => { await axios.delete(`/api/applications/${appId}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["match-stats"] });
    },
  });
}

// ── Free LLM preview (the "See full analysis" upgrade hook) ──────────────────

export interface PreviewStatus {
  enabled:   boolean;   // FREE_LLM_PREVIEW_ENABLED
  limit:     number;    // FREE_LLM_PREVIEW_COUNT
  used:      number;
  remaining: number;
}

export interface PreviewResult {
  match:   MatchWithJob;   // the same match, now with the full LLM breakdown
  preview: { used: number; remaining: number; costUsd: number };
}

/** Entitlement for the free full-LLM preview — drives whether the CTA shows. */
export function usePreviewStatus() {
  return useQuery<PreviewStatus>({
    queryKey: ["preview-status"],
    queryFn:  async () => {
      const { data } = await axios.get("/api/matches/preview/status");
      return data;
    },
  });
}

/** Spend the one free preview on a match. On success the match is returned with the
 *  full LLM breakdown; the caller swaps it in so the drawer re-renders the LLM tier. */
export function usePreviewMutation() {
  const qc = useQueryClient();
  return useMutation<PreviewResult, unknown, string>({
    mutationFn: async (matchId: string) => {
      const { data } = await axios.post(`/api/matches/${matchId}/preview`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["preview-status"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
      qc.invalidateQueries({ queryKey: ["match"] });
    },
  });
}
